import { Component, ChangeDetectionStrategy } from '@angular/core';
import {
  selectMembershipViewsForCurrentProject,
  IMembershipView,
} from '@core/state/entities/membership/membership.selectors';
import { Store } from '@ngrx/store';
import {
  Observable,
  tap,
  map,
  withLatestFrom,
  switchMap,
  firstValueFrom,
  EMPTY,
  catchError,
  filter,
  startWith,
  share,
} from 'rxjs';

import {
  EditEntityDialogComponent,
  IEditEntityDialogData,
} from '@app/project/project-admin/project-members/edit-entity-dialog/edit-entity-dialog.component';
import { ComponentStore } from '@ngrx/component-store';
import {
  selectPendingJoinRequestViewsForCurrentProject,
  IJoinRequestView,
  selectPendingJoinRequestsForCurrentProject,
} from '@core/state/entities/join-request/join-request.selectors';
import {
  IInvitationView,
  selectInvitationViewsForCurrentProject,
} from '@core/state/entities/invitation/invitation.selectors';
import {
  IInviteMembersDialogData,
  InviteMembersDialogComponent,
} from './invite-members-dialog/invite-members-dialog.component';
import {
  selectProjectGroupsForCurrentProject,
  selectSystemProjectGroupIdLookupMap,
} from '@core/state/entities/project-group/project-group.selectors';
import { selectCurrentProject } from '@core/state/entities/project/project.selectors';
import { MatDialog } from '@angular/material/dialog';
import { projectDetailActions } from '@core/actions/project-detail.actions';
import {
  IBulkConfirmationDialogData,
  BulkConfirmationDialogComponent,
} from '@app/project/project-admin/project-members/bulk-confirmation-dialog/bulk-confirmation-dialog.component';
import { formatDateTime, throwErrorExp } from '@core/actions/util';
import { ADMINISTRATOR_GROUP_NAME } from '@core/state/entities/project-group/project-group.entity';
import { FullNamePipe } from '@shared/pipes/full-name/full-name.pipe';
import { Actions } from '@ngrx/effects';
import { ActivatedRoute, ParamMap, Router, RouterLink } from '@angular/router';
import { fromPromise } from 'rxjs/internal/observable/innerFrom';
import { getRouteParamsForCachedComponent } from '@core/utils/router.utils';
import { v4 as uuid } from 'uuid';
import {
  FullName,
  PermissionReport,
} from '@core/services/api-client.generated';
import { selectCurrentProjectPermissionReport } from '@core/state/entities/project-details/project-details.selectors';
import { selectCurrentUserId } from '@core/auth/auth.selectors';
import { IInfoConfirmationDialogData } from '@shared/dialogs/info-confirmation-dialog/info-confirmation-dialog.component';
import { AsyncPipe } from '@angular/common';
import { MatMenuModule } from '@angular/material/menu';
import {
  MembershipTableComponent,
  TableSubtextDirective,
  SelectedItemsActionsDirective,
  ItemActionDirective,
} from './membership-table/membership-table.component';
import { MatButtonModule } from '@angular/material/button';
import { FlexLayoutModule } from '@ngbracket/ngx-layout';
import { PageComponent } from '../../../shared/page/page.component';

interface IMemberResponseDto {
  ids: string[];
  approve: boolean;
}
const loseYourAdminPrivilegesConfirmationDialog = (
  pluralSelection: boolean
): IInfoConfirmationDialogData => ({
  title: 'Update Your Membership',
  text: `You will lose your administrator privileges.`,
  informationBox: {
    warning: true,
    text: `${
      pluralSelection ? 'You have included yourself in the selection.\n' : ''
    }Leaving the Administrator group will result in the loss of administrator privileges.\nYou will need to request them back from the project owner or an administrator.`,
  },
});
const ownerLosingAdminPrivilegesConfirmationDialog = (
  pluralSelection: boolean
): IInfoConfirmationDialogData => ({
  title: 'Update Your Membership',
  text: `You will lose your administrator privileges.`,
  informationBox: {
    warning: true,
    text: `${
      pluralSelection ? 'You have included yourself in the selection.\n' : ''
    }Leaving the Administrator group will result in the loss of most administrator privileges.\nHowever as project owner you will retain the ability to assign project groups.
    \nTransfer of project ownership can be done (whilst Administrator) in Project Settings > General > Change Project Ownership.`,
  },
});
const assignAdminPrivilegesConfirmationDialog = (
  arg:
    | { pluralSelection: false; memberName: FullName }
    | { pluralSelection: true }
) => ({
  title: `Update Membership${arg.pluralSelection ? 's' : ''}`,
  text: `${
    arg.pluralSelection
      ? 'These members'
      : FullNamePipe.transform(
          (arg as { pluralSelection: false; memberName: FullName }).memberName
        )
  } will be given administrator privileges.`,
  informationBox: {
    warning: true,
    text: `Administrators have full access to all data in the project.
          This includes the ability to edit and delete data, and to add and remove other administrators.`,
  },
});

@Component({
  selector: 'app-project-members',
  templateUrl: './project-members.component.html',
  styleUrls: ['./project-members.component.scss'],
  changeDetection: ChangeDetectionStrategy.Eager,
  imports: [
    PageComponent,
    FlexLayoutModule,
    MatButtonModule,
    MembershipTableComponent,
    TableSubtextDirective,
    SelectedItemsActionsDirective,
    MatMenuModule,
    ItemActionDirective,
    RouterLink,
    AsyncPipe,
  ],
})
export class ProjectMembersComponent extends ComponentStore<object> {
  members$: Observable<IMembershipView[] | null> = this._store.select(
    selectMembershipViewsForCurrentProject
  );
  invitations$: Observable<IInvitationView[] | null> = this._store.select(
    selectInvitationViewsForCurrentProject
  );
  joinRequests$: Observable<IJoinRequestView[] | null> = this._store.select(
    selectPendingJoinRequestViewsForCurrentProject
  );
  projectId$: Observable<string | null> = this._store
    .select(selectCurrentProject)
    .pipe(map((p) => p?.id ?? null));

  projectPermissionReport$: Observable<PermissionReport | null> =
    this._store.select(selectCurrentProjectPermissionReport);

  readonly inviteNewMembers = this.effect<void>((inviteNewMembers$) =>
    inviteNewMembers$.pipe(
      withLatestFrom(
        this._store.select(selectProjectGroupsForCurrentProject),
        this._store.select(selectCurrentProject)
      ),
      tap(([_, projectGroups, project]) =>
        this._dialog.open(InviteMembersDialogComponent, {
          width: '680px',
          data: {
            projectGroups,
            projectId: project?.id,
          } as IInviteMembersDialogData,
        })
      )
    )
  );

  readonly revokeSelectedInvitations = this.effect<string[]>(
    (selectedInvitationIds$) =>
      selectedInvitationIds$.pipe(
        withLatestFrom(
          this._store.select(selectInvitationViewsForCurrentProject),
          this._store.select(selectCurrentProject)
        ),
        map(
          ([invitationIds, invitationViews, currentProject]) =>
            [
              invitationIds.map(
                (selId) =>
                  invitationViews?.find((inv) => inv.id === selId) ??
                  throwErrorExp(
                    `Could not find invitation with id ${selId} in invitation views`
                  )
              ),
              currentProject,
            ] as const
        ),
        tap(([invitationViews, currentProject]) =>
          this._dialog.open<
            BulkConfirmationDialogComponent,
            IBulkConfirmationDialogData,
            true | null
          >(BulkConfirmationDialogComponent, {
            data: {
              selectedEntityNames: invitationViews.map(
                (invitationView): string => invitationView.email
              ),
              confirmationText: `Confirm that you would like to revoke ${
                invitationViews.length === 1
                  ? 'this invitation'
                  : 'these invitations'
              }.`,
              infoBoxText: `The invitation${
                invitationViews.length === 1 ? '' : 's'
              } already sent will be revoked and the${
                invitationViews.length === 1 ? '' : 'se'
              } invitee${
                invitationViews.length === 1 ? '' : 's'
              } will no longer be able to access the project.`,
              negativeAction: true,
              confirmationButtonText: `Revoke invitation${
                invitationViews.length === 1 ? '' : 's'
              }`,
              singularEntityType: 'invitation sent to',
              pluralEntityType: 'invitations sent to',
              definiteArticle: true,
              dialogIcon: 'group',
              dispatchAction: projectDetailActions.revokeInvitationsBulk({
                projectId:
                  currentProject?.id ?? throwErrorExp('Project not found'),
                inviteeEmails: invitationViews.map((inv) => inv.email),
                correlationId: uuid(),
              }),
              successAction:
                projectDetailActions.invitationsBulkRevokedSuccessfully,
              errorAction: projectDetailActions.errorBulkRevokingInvitations,
            },
            width: '550px',
            autoFocus: false,
            restoreFocus: false,
          })
        )
      )
  );

  readonly respondSelectedJoinRequests = this.effect<IMemberResponseDto>(
    (selectedJoinRequestIds$: Observable<IMemberResponseDto>) =>
      selectedJoinRequestIds$.pipe(
        withLatestFrom(
          this._store.select(selectPendingJoinRequestViewsForCurrentProject),
          this._store.select(selectCurrentProject),
          this._store.select(selectPendingJoinRequestsForCurrentProject)
        ),
        map(
          ([
            { ids: selectedJoinRequestIds, approve },
            joinRequestViews,
            currentProject,
            joinRequests,
          ]) =>
            [
              selectedJoinRequestIds.map(
                (selId) =>
                  joinRequestViews?.find((jr) => jr.id === selId) ??
                  throwErrorExp(
                    `Could not find join request with id ${selId} in joinRequests`
                  )
              ),
              approve,
              currentProject,
              joinRequests,
            ] as const
        ),
        tap(([joinRequestViews, approve, currentProject, joinRequests]) =>
          this._dialog.open<
            BulkConfirmationDialogComponent,
            IBulkConfirmationDialogData,
            true | null
          >(BulkConfirmationDialogComponent, {
            data: {
              selectedEntityNames: joinRequestViews.map(
                (joinRequestView): string =>
                  FullNamePipe.transform(joinRequestView.investigatorName) ??
                  throwErrorExp('Could not transform name')
              ),
              confirmationText: `Confirm that you would like to ${
                approve ? 'approve' : 'decline'
              } ${
                joinRequestViews.length === 1
                  ? 'this request '
                  : 'these requests '
              } to join this project.`,
              confirmationButtonText: `${
                approve ? 'Approve request' : 'Decline request'
              }${joinRequestViews.length === 1 ? '' : 's'}`,
              singularEntityType: 'a join request from',
              pluralEntityType: 'join requests from',
              definiteArticle: false,
              dialogIcon: approve ? 'group_add' : 'group_off',
              dispatchAction: projectDetailActions.respondToJoinRequests({
                projectId:
                  currentProject?.id ?? throwErrorExp('Project not found'),
                investigatorIds: joinRequestViews.map(
                  (jr) =>
                    joinRequests?.find((j) => j.id === jr.id)?.requestedById ??
                    throwErrorExp('Join request not found')
                ),
                approve,
                correlationId: uuid(),
              }),
              successAction: projectDetailActions.joinRequestsResponseSent,
              errorAction: projectDetailActions.errorRespondingToJoinRequests,
            },
            width: '550px',
            autoFocus: false,
            restoreFocus: false,
          })
        )
      )
  );
  readonly updateSelectedMembers = this.effect<{
    selectedMemberIds: string[];
    administrator: boolean;
  }>((membershipAssignments$) =>
    membershipAssignments$.pipe(
      withLatestFrom(
        this._store.select(selectMembershipViewsForCurrentProject),
        this._store.select(selectCurrentProject),
        this._store.select(selectSystemProjectGroupIdLookupMap)
      ),
      map(
        ([
          membershipAssignments,
          memberships,
          currentProject,
          projectGroupLookup,
        ]) =>
          [
            membershipAssignments.selectedMemberIds.map(
              (selId): IMembershipView =>
                memberships?.find((mem) => mem.id === selId) ??
                throwErrorExp(
                  `Could not find membership with id ${selId} in memberships`
                )
            ),
            membershipAssignments.administrator,
            currentProject,
            projectGroupLookup,
          ] as const
      ),
      tap(
        ([
          selectedMembershipViews,
          assignAdministrator,
          currentProject,
          projectGroupLookup,
        ]) => {
          return this._dialog.open<
            BulkConfirmationDialogComponent,
            IBulkConfirmationDialogData,
            true | null
          >(BulkConfirmationDialogComponent, {
            data: {
              selectedEntityNames: selectedMembershipViews.map(
                (membershipView): string =>
                  FullNamePipe.transform(membershipView.investigatorName) ??
                  throwErrorExp('Investigator name not found')
              ),
              confirmationText: `Confirm that you would like to make ${
                selectedMembershipViews.length === 1
                  ? (selectedMembershipViews[0].isCurrent
                      ? 'yourself '
                      : 'this member ') +
                    (assignAdministrator ? 'an administrator' : 'a reviewer')
                  : 'these members ' +
                    (assignAdministrator ? 'administrators' : 'reviewers')
              } of this project.`,
              confirmationDialog:
                !assignAdministrator &&
                selectedMembershipViews.some(
                  (membership) => membership.isCurrent
                )
                  ? selectedMembershipViews.some(
                      (mem) => mem.isCurrent && mem.isOwner
                    )
                    ? ownerLosingAdminPrivilegesConfirmationDialog(
                        selectedMembershipViews.length > 1
                      )
                    : loseYourAdminPrivilegesConfirmationDialog(
                        selectedMembershipViews.length > 1
                      )
                  : undefined,
              confirmationButtonText: `${
                assignAdministrator ? 'Make administrator' : 'Make reviewer'
              }${selectedMembershipViews.length === 1 ? '' : 's'}`,
              singularEntityType: 'member',
              pluralEntityType: 'members',
              definiteArticle: true,
              dialogIcon: 'group',
              dispatchAction:
                projectDetailActions.updateProjectMembershipsGroupBulk({
                  projectId:
                    currentProject?.id ?? throwErrorExp('Project not found'),
                  investigatorIds: selectedMembershipViews.map(
                    (mem) => mem.investigatorId
                  ),
                  groupIds: assignAdministrator
                    ? [
                        projectGroupLookup.get(ADMINISTRATOR_GROUP_NAME) ??
                          throwErrorExp('Could not find administrators group'),
                      ]
                    : [],
                }),
              successAction:
                projectDetailActions.projectMembershipsGroupBulkUpdatedSuccessfully,
              errorAction:
                projectDetailActions.errorUpdatingProjectMembershipsGroupBulk,
            },
            width: '550px',
            autoFocus: false,
            restoreFocus: false,
          });
        }
      )
    )
  );

  readonly updateSelectedInvitations = this.effect<{
    selectedInvitationIds: string[];
    administrator: boolean;
  }>((invitationAssignments$) =>
    invitationAssignments$.pipe(
      withLatestFrom(
        this._store.select(selectInvitationViewsForCurrentProject),
        this._store.select(selectCurrentProject),
        this._store.select(selectSystemProjectGroupIdLookupMap)
      ),
      map(
        ([
          invitationAssignments,
          invitationViews,
          currentProject,
          projectGroupLookup,
        ]) =>
          [
            invitationAssignments.selectedInvitationIds.map(
              (selId) =>
                invitationViews?.find((inv) => inv.id === selId) ??
                throwErrorExp(
                  `Could not find invitation with id ${selId} in invitation views`
                )
            ),
            invitationAssignments.administrator,
            currentProject,
            projectGroupLookup,
          ] as const
      ),
      switchMap(
        ([
          invitationViews,
          assignAdministrator,
          currentProject,
          projectGroupLookup,
        ]) =>
          this._dialog
            .open<
              BulkConfirmationDialogComponent,
              IBulkConfirmationDialogData,
              true | null
            >(BulkConfirmationDialogComponent, {
              data: {
                selectedEntityNames: invitationViews.map(
                  (invitationView): string => invitationView.email
                ),
                confirmationText: `Confirm that you would like to invite ${
                  invitationViews.length === 1
                    ? 'this invitee as ' +
                      (assignAdministrator ? 'an administrator' : 'a reviewer')
                    : 'these invitees as ' +
                      (assignAdministrator ? 'administrators' : 'reviewers')
                } of this project.`,
                confirmationButtonText: `${
                  assignAdministrator
                    ? 'Invite as administrator'
                    : 'Invite as reviewer'
                }${invitationViews.length === 1 ? '' : 's'}`,
                singularEntityType: 'invitation sent to',
                pluralEntityType: 'invitations sent to',
                definiteArticle: true,
                dialogIcon: 'group',
                dispatchAction: projectDetailActions.updateInvitationGroupsBulk(
                  {
                    projectId:
                      currentProject?.id ?? throwErrorExp('Project not found'),
                    inviteeEmails: invitationViews.map((inv) => inv.email),
                    groupIds: assignAdministrator
                      ? [
                          projectGroupLookup.get(ADMINISTRATOR_GROUP_NAME) ??
                            throwErrorExp(
                              'Could not find administrators group'
                            ),
                        ]
                      : [],
                    correlationId: uuid(),
                  }
                ),
                successAction:
                  projectDetailActions.invitationGroupsBulkUpdatedSuccessfully,
                errorAction:
                  projectDetailActions.errorBulkUpdatingInvitationGroups,
              },
              width: '550px',
              autoFocus: false,
              restoreFocus: false,
            })
            .afterClosed()
      )
    )
  );

  constructor(
    private _dialog: MatDialog,
    private _store: Store,
    private _actions: Actions,
    private _activatedRoute: ActivatedRoute,
    private _router: Router
  ) {
    super({});
    this.effect(() =>
      getRouteParamsForCachedComponent(
        this._router,
        ProjectMembersComponent
      ).pipe(
        startWith(_activatedRoute.snapshot.paramMap),
        filter((paramMap) => paramMap.has('entityType')),
        map((paramMap) => [
          paramMap.get('entityType') as string,
          paramMap.get('entityId'),
          paramMap.get('projectId'),
        ]),
        switchMap(([entityType, entityId, projectId]) =>
          entityId
            ? fromPromise(
                entityType === 'members'
                  ? this.viewMembership(entityId)
                  : entityType === 'invitations'
                    ? this.viewInvitation(decodeURIComponent(entityId))
                    : entityType === 'join-requests'
                      ? this.viewJoinRequest(entityId)
                      : Promise.reject('Invalid entity type')
              ).pipe(
                tap(() => {
                  this._router.navigate([
                    'projects',
                    projectId,
                    'admin',
                    'membership',
                  ]);
                }),
                catchError(
                  (err) => (
                    console.error(err),
                    this._router.navigate(['not-found']),
                    EMPTY
                  )
                )
              )
            : throwErrorExp('Entity id not found')
        )
      )
    );
  }

  async viewMembership(investigatorId: string): Promise<void> {
    const membershipViewsForCurrentProject = await firstValueFrom(
      this._store.select(selectMembershipViewsForCurrentProject)
    );

    const projectGroupsForCurrentProject = await firstValueFrom(
      this._store.select(selectProjectGroupsForCurrentProject)
    );
    const membership =
      membershipViewsForCurrentProject?.find(
        (mem) => mem.investigatorId === investigatorId
      ) ?? throwErrorExp('Membership not found');
    const currentUserId = await firstValueFrom(
      this._store.select(selectCurrentUserId)
    );
    const isCurrent = investigatorId === currentUserId;

    const dialogResult = await firstValueFrom(
      this._dialog
        .open<EditEntityDialogComponent, IEditEntityDialogData, boolean>(
          EditEntityDialogComponent,
          {
            data: {
              name: FullNamePipe.transform(
                membership.investigatorName
              ) as string,
              description: `${
                membership?.isCurrent
                  ? 'You'
                  : (FullNamePipe.transform(
                      membership.investigatorName
                    ) as string)
              } joined the project on ${formatDateTime(
                membership.dateTimeCreated
              )}`,
              dialogIcon: 'person',
              isOwner: membership.isOwner,
              isCurrent,
              entityLabel: 'Membership',
              projectGroups: projectGroupsForCurrentProject,
              dismissButtonText: 'Close',
              originalGroups: membership.groups,
              primaryActivity: {
                buttonText: 'Save',
                dispatchAction: ({ newProjectGroupIds }) =>
                  projectDetailActions.updateProjectMembershipGroups({
                    investigatorId: membership.investigatorId,
                    projectId: membership.projectId,
                    groupIds:
                      newProjectGroupIds ??
                      throwErrorExp('project group Ids should be defined'),
                    correlationId: uuid(),
                  }),
                successActions:
                  projectDetailActions.projectMembershipGroupsUpdated,
                errorActions:
                  projectDetailActions.errorUpdatingProjectMembershipGroups,
                confirmationDialog: (isAdmin) =>
                  isAdmin && !membership.isCurrent
                    ? assignAdminPrivilegesConfirmationDialog({
                        pluralSelection: false,
                        memberName: membership.investigatorName,
                      })
                    : !membership.isOwner && membership.isCurrent && !isAdmin
                      ? loseYourAdminPrivilegesConfirmationDialog(false)
                      : membership.isOwner && membership.isCurrent && !isAdmin
                        ? ownerLosingAdminPrivilegesConfirmationDialog(false)
                        : null,
              },
            } as IEditEntityDialogData,
          }
        )
        .afterClosed()
    );
  }

  async viewInvitation(inviteeEmail: string): Promise<void> {
    const invitationViewsForCurrentProject = await firstValueFrom(
      this._store.select(selectInvitationViewsForCurrentProject)
    );
    const projectGroups = await firstValueFrom(
      this._store.select(selectProjectGroupsForCurrentProject)
    );
    const project = await firstValueFrom(
      this._store.select(selectCurrentProject)
    );
    const invitationView =
      invitationViewsForCurrentProject?.find(
        (inv) => inv.email === inviteeEmail
      ) ?? throwErrorExp('Invitation not found');
    const dialogResult = await firstValueFrom(
      this._dialog
        .open<EditEntityDialogComponent, IEditEntityDialogData, boolean>(
          EditEntityDialogComponent,
          {
            data: {
              dialogIcon: 'email',
              name: invitationView.email,
              entityLabel: 'Invitation',
              dismissButtonText: 'close',
              projectGroupHint:
                'Invitee will join the project with this project group.',
              projectGroups,
              originalGroups: invitationView.groups,
              description: invitationView.lastEmailSent
                ? `Invitation last sent at ${formatDateTime(
                    invitationView.lastEmailSent.date
                  )}`
                : 'Invitation has not been sent yet',
              message: invitationView.message,
              secondaryActivity: {
                buttonText: 'Resend',
                dispatchAction: ({ newProjectGroupIds, message }) =>
                  projectDetailActions.resendInvitationsBulk({
                    inviteeEmails: [invitationView.email],
                    projectId:
                      project?.id ?? throwErrorExp('Project not found'),
                    groupIds: newProjectGroupIds,
                    message,
                    correlationId: uuid(),
                  }),
                successActions:
                  projectDetailActions.invitationsBulkResentSuccessfully,
                errorActions:
                  projectDetailActions.errorBulkResendingInvitations,
              },
              negativeActivity: {
                buttonText: 'Revoke',
                dispatchAction: () =>
                  projectDetailActions.revokeInvitationsBulk({
                    correlationId: uuid(),
                    inviteeEmails: [invitationView.email],
                    projectId:
                      project?.id ?? throwErrorExp('Project not found'),
                  }),
                successActions:
                  projectDetailActions.invitationsBulkRevokedSuccessfully,
                errorActions: projectDetailActions.errorBulkRevokingInvitations,
                confirmationDialog: () => ({
                  title: 'Revoke Invitation',
                  text: `The invitation sent to ${invitationView.email} will be revoked \
                         and they will no longer be able to access the project.`,
                  informationBox: {
                    text: `An invitation has already been sent to ${invitationView.email}.\n
                          This will no longer work and they will receive a message saying that \
                          their invitation has been revoked.`,
                  },
                }),
              },
              primaryActivity: {
                buttonText: 'Save',
                dispatchAction: ({ newProjectGroupIds }) =>
                  projectDetailActions.updateInvitationGroupsBulk({
                    inviteeEmails: [invitationView.email],
                    projectId:
                      project?.id ?? throwErrorExp('Project not found'),
                    groupIds:
                      newProjectGroupIds ??
                      throwErrorExp('project group Ids should be defined'),
                    correlationId: uuid(),
                  }),
                successActions:
                  projectDetailActions.invitationGroupsBulkUpdatedSuccessfully,
                errorActions:
                  projectDetailActions.errorBulkUpdatingInvitationGroups,
                confirmationDialog: (isAdmin) =>
                  isAdmin
                    ? {
                        title: 'Provide administrator privileges',
                        text: `The invitee ${invitationView.email} will be given administrator privileges.`,
                        informationBox: {
                          warning: true,
                          text: `Administrators have full access to all data in the project.
                          This includes the ability to edit and delete data, and to add and \
                          remove other administrators.`,
                        },
                      }
                    : null,
              },
            },
            width: '500px',
          }
        )
        .afterClosed()
    );
  }

  async viewJoinRequest(investigatorId: string): Promise<void> {
    const joinRequestsForCurrentProject = await firstValueFrom(
      this._store.select(selectPendingJoinRequestViewsForCurrentProject)
    );
    const projectGroupsForCurrentProject = await firstValueFrom(
      this._store.select(selectProjectGroupsForCurrentProject)
    );
    const joinRequest =
      joinRequestsForCurrentProject?.find(
        (jr) => jr.requestedById === investigatorId
      ) ?? throwErrorExp('Join request not found');
    const dialogResult = await firstValueFrom(
      this._dialog
        .open<EditEntityDialogComponent, IEditEntityDialogData, boolean>(
          EditEntityDialogComponent,
          {
            data: {
              name: FullNamePipe.transform(
                joinRequest.investigatorName
              ) as string,
              description: `${
                FullNamePipe.transform(joinRequest.investigatorName) as string
              } requested to join the project on ${formatDateTime(
                joinRequest.dateTimeCreated
              )}`,
              message: joinRequest.message,
              dialogIcon: 'person',
              entityLabel: 'Join Request',
              originalGroups: joinRequest.groups,
              projectGroups: projectGroupsForCurrentProject,
              dismissButtonText: 'Close',
              primaryActivity: {
                buttonText: 'Approve',
                dispatchAction: ({ newProjectGroupIds }) =>
                  projectDetailActions.respondToJoinRequests({
                    investigatorIds: [joinRequest.requestedById],
                    projectId: joinRequest.projectId,
                    approve: true,
                    groupIds:
                      newProjectGroupIds ??
                      throwErrorExp('project group Ids should be defined'),
                    correlationId: uuid(),
                  }),
                successActions: projectDetailActions.joinRequestsResponseSent,
                errorActions:
                  projectDetailActions.errorRespondingToJoinRequests,
                independentOfProjectGroupChange: true,
              },
              negativeActivity: {
                buttonText: 'Decline',
                dispatchAction: () =>
                  projectDetailActions.respondToJoinRequests({
                    investigatorIds: [joinRequest.requestedById],
                    projectId: joinRequest.projectId,
                    approve: false,
                    correlationId: uuid(),
                  }),
                successActions: projectDetailActions.joinRequestsResponseSent,
                errorActions:
                  projectDetailActions.errorRespondingToJoinRequests,
                confirmationDialog: () => ({
                  title: 'Decline Join Request',
                  text: `${FullNamePipe.transform(
                    joinRequest.investigatorName
                  )}'s request to join this project will be declined.`,
                  informationBox: {
                    text: `${FullNamePipe.transform(
                      joinRequest.investigatorName
                    )} will receive an email informing them that their request to join \
                        the project has been declined.`,
                  },
                }),
              },
            },
            width: '500px',
          }
        )

        .afterClosed()
    );
  }
  navigate(entityType: string, tableId: string) {
    this._router.navigate([`${entityType}/${encodeURIComponent(tableId)}`], {
      relativeTo: this._activatedRoute,
    });
  }
}
